import * as React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import dayjs from "dayjs";
import { fetchTherapistReports, fetchTherapistReportLink } from "../../Services/apiService";
import PortalPagination from "../../components/portal/PortalPagination";
import ReportsFilterBar from "../../components/portal/ReportsFilterBar";
import ViewDownloadButtons from "../../components/portal/ViewDownloadButtons";
import { C, compactCardSx } from "../../theme/dashboardCardTokens";
import { useTranslation } from "../../i18n/LanguageContext";

// The backend always returns newest-completed_at-first and exposes no sort
// param, so clicking a column header below only reorders the rows already
// on the current page (order_number / test_identifier / substrate / completed_at).
function compareReports(a, b, field) {
  if (field === "completed_at") {
    return (a.completed_at ? new Date(a.completed_at).getTime() : 0) -
      (b.completed_at ? new Date(b.completed_at).getTime() : 0);
  }
  if (field === "order_number") {
    return (Number(a.order_number) || 0) - (Number(b.order_number) || 0);
  }
  const av = (a[field] || "").toString().toLowerCase();
  const bv = (b[field] || "").toString().toLowerCase();
  if (av < bv) return -1;
  if (av > bv) return 1;
  return 0;
}

// Same list layout as My Reports.
function TherapistReports() {
  const { t } = useTranslation();
  const [reports, setReports] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");

  const [sortField, setSortField] = React.useState(null);
  const [sortDirection, setSortDirection] = React.useState("asc");

  const hasActiveFilter = Boolean(search || startDate || endDate);

  // Debounce the free-text search (400ms), same pattern as the other portal search boxes.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDateRangeChange = (nextStartDate, nextEndDate) => {
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTherapistReports(page, 20, { search, startDate, endDate })
      .then((res) => {
        if (cancelled) return;
        setReports(res.data?.reports || []);
        setPagination(res.data?.pagination || null);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("fetchTherapistReports failed:", { status: err.status, message: err.message, err });
          setError(err.message || t("portal.reports.therapist.loadError"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, startDate, endDate]);

  const sortedReports = React.useMemo(() => {
    if (!sortField) return reports;
    const sorted = [...reports].sort((a, b) => compareReports(a, b, sortField));
    return sortDirection === "asc" ? sorted : sorted.reverse();
  }, [reports, sortField, sortDirection]);

  const sortHeaderProps = (field) => ({
    active: sortField === field,
    direction: sortField === field ? sortDirection : "asc",
    onClick: () => handleSort(field),
  });

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t("portal.reports.therapist.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("portal.reports.therapist.subtitle")}
      </Typography>

      <ReportsFilterBar
        search={searchInput}
        onSearchChange={setSearchInput}
        resultCount={!loading ? pagination?.total : null}
        loading={loading}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
      />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && reports.length === 0 && (
        <Box sx={{ p: 4, textAlign: "center", color: C.textSecondary }}>
          {hasActiveFilter ? t("portal.reports.emptyFiltered") : t("portal.reports.empty")}
        </Box>
      )}

      {!loading && !error && reports.length > 0 && (
        <TableContainer component={Paper} sx={{ ...compactCardSx, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortField === "order_number" ? sortDirection : false}>
                  <TableSortLabel {...sortHeaderProps("order_number")}>
                    {t("portal.reports.table.order")}
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === "test_identifier" ? sortDirection : false}>
                  <TableSortLabel {...sortHeaderProps("test_identifier")}>
                    {t("portal.reports.table.test")}
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === "substrate" ? sortDirection : false}>
                  <TableSortLabel {...sortHeaderProps("substrate")}>
                    {t("portal.reports.table.substrate")}
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === "completed_at" ? sortDirection : false}>
                  <TableSortLabel {...sortHeaderProps("completed_at")}>
                    {t("portal.reports.table.completed")}
                  </TableSortLabel>
                </TableCell>
                <TableCell>{t("portal.reports.table.report")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedReports.map((report) => (
                <TableRow key={report.lab_test_id}>
                  <TableCell>{report.order_number}</TableCell>
                  <TableCell>{report.test_identifier}</TableCell>
                  <TableCell>{report.substrate}</TableCell>
                  <TableCell>
                    {report.completed_at ? dayjs(report.completed_at).format("MMM D, YYYY") : "—"}
                  </TableCell>
                  <TableCell>
                    <ViewDownloadButtons
                      id={report.lab_test_id}
                      filename={`Sibolab-Report-${report.test_identifier}.pdf`}
                      getLink={fetchTherapistReportLink}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && <PortalPagination pagination={pagination} onChange={setPage} />}
    </Box>
  );
}

export default TherapistReports;
